import React, { memo, useCallback } from 'react';
import { LeftToRightIcon } from '../../icons/left-to-right';
import { RightToLeftIcon } from '../../icons/right-to-left';
import { TextDirection } from '../../items/text/text-item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { InspectorSubLabel } from '../components/inspector-label';
import { useSelectionValues } from '../utils/use-selection-values';
import { TextItem } from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';

const TextDirectionControlsUnmemoized: React.FC<{
  direction: TextDirection;
  itemId: string;
}> = ({ direction, itemId }) => {
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<
    TextItem | CaptionsItem,
    TextDirection
  >(itemId, (item) => item.direction);

  const setDirection = useCallback(
    (newDirection: TextDirection) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text' && i.type !== 'captions') {
              throw new Error(
                'Text direction can only be changed for text items',
              );
            }

            return {
              ...i,
              direction: newDirection,
            };
          });
        },
        commitToUndoStack: true,
      });
    },
    [setState, itemId, targetIds],
  );

  return (
    <div className="flex-1">
      <InspectorSubLabel>Direction</InspectorSubLabel>
      <div className="inline-flex items-center gap-2xs editor-starter-field hover:border-transparent">
        <button
          onClick={() => setDirection('ltr')}
          data-active={!isMixed && direction === 'ltr'}
          className="flex h-6 w-10 items-center justify-center rounded-[2px] editor-starter-focus-ring text-neutral-300 data-[active=true]:bg-black/20 data-[active=true]:outline data-[active=true]:outline-neutral-700"
          title="Left to Right"
          aria-label="Left to Right"
        >
          <LeftToRightIcon />
        </button>
        <button
          onClick={() => setDirection('rtl')}
          data-active={!isMixed && direction === 'rtl'}
          className="flex h-6 w-10 items-center justify-center rounded-[2px] editor-starter-focus-ring text-neutral-300 data-[active=true]:bg-black/20 data-[active=true]:outline data-[active=true]:outline-neutral-700"
          title="Right to Left"
          aria-label="Right to Left"
        >
          <RightToLeftIcon />
        </button>
        {isMixed && (
          <span className="text-2xs text-comp-moderate-medium">(Mixed)</span>
        )}
      </div>
    </div>
  );
};

export const TextDirectionControls = memo(TextDirectionControlsUnmemoized);
